import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import api from '../utils/httpClient';

export interface UserConfigDb {
  userId: string;
  EnableClassroom: boolean;
  EnableSalesforceImport: boolean;
  CanAccessReports: boolean;
  CanAccessGroups: boolean;
  IsAdmin?: boolean;
  AccountAccessGroups?: boolean;
}

export interface UserAccessConfig {
  saving?: boolean;
  error?: string;
  success?: boolean;
  allow?: boolean;
}

export interface UserSettings {
  allowClassSession: UserAccessConfig;
  allowSalesforceImport: UserAccessConfig;
  allowReports: UserAccessConfig;
  allowGroups: UserAccessConfig;
  userConfig: any;
}

export interface InitialState {
  saving: boolean;
  error: string;
  success: boolean;
  configurations: UserSettings;
}

export interface UserInfo {

  FirstName: string
  LastName: string
  Email: string
  BargeInCall: boolean
  IsOnDuty: boolean
  IsAdmin: boolean
  Created: boolean
  OnlyUrgent: boolean
  Telephone: string
  CanSeeClientsTab: boolean
  CanSeeMessageCenterTab: boolean
  CanSeeQueueTab: boolean
  CanSeeMemoryBox: boolean
  CanSeeBulletinBoard: boolean
  CanSeeAlertsTab: boolean
  CanSeeStaffTab: boolean
  CanDeletePatients: boolean
  CanDeleteDevices: boolean
  CanSeeDevicesTab: boolean
  EnableClassroom: boolean
  CanRestartDevices: boolean
  CanSendMessageToAllPatients: boolean;
}

interface EnableConfigPayload {
  userId: string;
  currentStatus: boolean;
}


export const enableClassSessionAccessToUser = createAsyncThunk(
  'userConfig/enableClassSessionAccessToUser',
  async (params: EnableConfigPayload) => {
    const { userId, currentStatus } = params;
    await api.put(`/user/${userId}/enable_class_session`, {
      newStatus: currentStatus,
    });
    return {
      userId,
      currentStatus,
    };
  }
);


export const updateUserConfigPromise = async (userId: string, data: any) => {

  return api.put(`/user/${userId}/config`, data);
};

export const enableSalesforceImportToUser = createAsyncThunk(
  'userConfig/enableSalesforceImportToUser',
  async (params: EnableConfigPayload) => {
    const { userId, currentStatus } = params;
    await api.put(`/user/${userId}/enable_salesforce_import`, {
      newStatus: currentStatus,
    });
    return {
      userId,
      currentStatus,
    };
  }
);

export const enableReportsToUser = createAsyncThunk(
  'userConfig/enableReportsToUser',
  async (params: EnableConfigPayload) => {
    const { userId, currentStatus } = params;
    await api.put(`/user/${userId}/enable_reports`, {
      newStatus: currentStatus,
    });
    return {
      userId,
      currentStatus,
    };
  }
);
export const getUserConfigPromise = (userId: string) => {
  return api.get(`/user/${userId}/config`);

}

export const getUserConfig = createAsyncThunk(
  'useConfig/getUserConfig',
  async (userId: string) => {
    const { data } = await getUserConfigPromise(userId);
    return {
      userId,
      ...data,
    } as UserConfigDb;
  }
);

const initialState: InitialState = {
  saving: false,
  error: '',
  success: false,
  configurations: {
    allowClassSession: {},
    allowSalesforceImport: {},
    allowReports: {},
    allowGroups: {},
    userConfig: {},
  },
};

const userConfigSlice = createSlice({
  name: 'userConfig',
  initialState,
  reducers: {
    reset: () => initialState,
    clearUserConfig: (state) => {
      state.configurations.allowClassSession.saving = false;
      state.configurations.allowClassSession.success = false;
      state.configurations.allowClassSession.error = '';
      state.configurations.allowSalesforceImport.saving = false;
      state.configurations.allowSalesforceImport.success = false;
      state.configurations.allowSalesforceImport.error = '';
      state.configurations.allowReports.saving = false;
      state.configurations.allowReports.success = false;
      state.configurations.allowReports.error = '';
      state.configurations.allowGroups.saving = false;
      state.configurations.allowGroups.success = false;
      state.configurations.allowGroups.error = '';
    },
  },
  extraReducers: (builder) => {
    // getUserConfig
    builder.addCase(getUserConfig.pending, (state) => {
      state.saving = true;
      state.success = false;
      state.error = '';
    });
    builder.addCase(
      getUserConfig.fulfilled,
      (state, action: PayloadAction<UserConfigDb>) => {
        const { ...config } = action.payload;
        state.saving = false;
        state.success = true;
        state.error = '';
        state.configurations.userConfig = config;
        state.configurations.allowClassSession.allow = config.EnableClassroom;
        state.configurations.allowSalesforceImport.allow = config.EnableSalesforceImport;
        state.configurations.allowReports.allow = config.CanAccessReports;
        state.configurations.allowGroups.allow = config.CanAccessGroups;
      }
    );
    builder.addCase(getUserConfig.rejected, (state, action) => {
      console.error(action.error);
      state.saving = false;
      state.success = false;
      state.error = 'Unable to get configurations of the user';
    });
    // enableClassSessionAccessToUser
    builder.addCase(enableClassSessionAccessToUser.pending, (state) => {
      state.configurations.allowClassSession.saving = true;
      state.configurations.allowClassSession.success = false;
      state.configurations.allowClassSession.error = '';
    });
    builder.addCase(
      enableClassSessionAccessToUser.fulfilled,
      (state, action: PayloadAction<EnableConfigPayload>) => {
        const { currentStatus } = action.payload;
        state.configurations.allowClassSession.saving = false;
        state.configurations.allowClassSession.success = true;
        state.configurations.allowClassSession.error = '';
        state.configurations.allowClassSession.allow = currentStatus;
      }
    );
    builder.addCase(
      enableClassSessionAccessToUser.rejected,
      (state, action) => {
        console.error(action.error);
        console.log(action);
        state.configurations.allowClassSession.saving = false;
        state.configurations.allowClassSession.success = false;
        state.configurations.allowClassSession.error =
          'Unable to grant access to class session';
      }
    );

    // enableSalesforceImportToUser
    builder.addCase(enableSalesforceImportToUser.pending, (state) => {
      state.configurations.allowSalesforceImport.saving = true;
      state.configurations.allowSalesforceImport.success = false;
      state.configurations.allowSalesforceImport.error = '';
    });
    builder.addCase(
      enableSalesforceImportToUser.fulfilled,
      (state, action: PayloadAction<EnableConfigPayload>) => {
        const { currentStatus } = action.payload;
        state.configurations.allowSalesforceImport.saving = false;
        state.configurations.allowSalesforceImport.success = true;
        state.configurations.allowSalesforceImport.error = '';
        state.configurations.allowSalesforceImport.allow = currentStatus;
      }
    );
    builder.addCase(
      enableSalesforceImportToUser.rejected,
      (state, action) => {
        console.error(action.error);
        console.log(action);
        state.configurations.allowSalesforceImport.saving = false;
        state.configurations.allowSalesforceImport.success = false;
        state.configurations.allowSalesforceImport.error =
          'Unable to grant access to salesforce import';
      }
    );

    // enableReportsToUser
    builder.addCase(enableReportsToUser.pending, (state) => {
      state.configurations.allowReports.saving = true;
      state.configurations.allowReports.success = false;
      state.configurations.allowReports.error = '';
    });
    builder.addCase(
      enableReportsToUser.fulfilled,
      (state, action: PayloadAction<EnableConfigPayload>) => {
        const { currentStatus } = action.payload;
        state.configurations.allowReports.saving = false;
        state.configurations.allowReports.success = true;
        state.configurations.allowReports.error = '';
        state.configurations.allowReports.allow = currentStatus;
      }
    );
    builder.addCase(
      enableReportsToUser.rejected,
      (state, action) => {
        console.error(action.error);
        console.log(action);
        state.configurations.allowReports.saving = false;
        state.configurations.allowReports.success = false;
        state.configurations.allowReports.error =
          'Unable to grant access to reports';
      }
    );
  },
});

export const { reset, clearUserConfig } = userConfigSlice.actions;
export default userConfigSlice;
